import { format, isSameDay } from 'date-fns';
import { cn } from '@/lib/cn';
import { getMiniMonth } from '@/lib/weekCalendar';
import type { CalendarVisualItem } from '@/components/calendar/views/WeekTimeGrid';
import { CALENDAR_YEAR_DATE_CELL_CLASS } from '@/components/calendar/shared/calendarTheme';

type CalendarVisualKind = CalendarVisualItem['kind'];

const KIND_DOT: Record<CalendarVisualKind, string> = {
  event: 'bg-blue-500',
  meeting: 'bg-fuchsia-500',
  task: 'bg-orange-500',
  note: 'bg-sky-500',
  'org-event': 'bg-violet-500',
  holiday: 'bg-emerald-500',
  schedule: 'bg-slate-500',
  'pms-task': 'bg-purple-500',
  'pms-project': 'bg-cyan-500',
};

const KIND_ORDER: CalendarVisualKind[] = ['meeting', 'event', 'org-event', 'holiday', 'task', 'note', 'schedule', 'pms-task', 'pms-project'];

type Props = {
  year: number;
  selectedDate: Date | null;
  items: CalendarVisualItem[];
  attendanceByDay?: Record<string, 'present' | 'absent' | 'weekoff' | 'holiday'> | null;
  onSelectDate: (date: Date) => void;
  onSelectMonth: (monthAnchor: Date) => void;
  compact?: boolean;
};

export function YearViewGrid({
  year,
  selectedDate,
  items,
  attendanceByDay,
  onSelectDate,
  onSelectMonth,
  compact = false,
}: Props) {
  const itemKindByDay = new Map<string, CalendarVisualKind>();

  for (const item of items) {
    const key = item.dateKey;
    if (!itemKindByDay.has(key)) {
      itemKindByDay.set(key, item.kind);
      continue;
    }
    const existing = itemKindByDay.get(key)!;
    const existingRank = KIND_ORDER.indexOf(existing);
    const newRank = KIND_ORDER.indexOf(item.kind);
    if (newRank < existingRank) itemKindByDay.set(key, item.kind);
  }

  const monthCards = Array.from({ length: 12 }, (_, monthIdx) => {
    const monthAnchor = new Date(year, monthIdx, 1);
    const miniMonth = getMiniMonth(monthAnchor);
    const monthSelected = selectedDate ? selectedDate.getFullYear() === year && selectedDate.getMonth() === monthIdx : false;
    const monthTitle = format(monthAnchor, 'MMMM');

    return (
      <div
        key={monthIdx}
        className={cn(
          'group overflow-hidden rounded-3xl border bg-white p-4 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md dark:bg-card',
          monthSelected
            ? 'border-[#5B6CF6]/50 ring-2 ring-[#5B6CF6]/20 dark:border-[#8B7CFF]/50 dark:ring-[#8B7CFF]/20'
            : 'border-slate-100 hover:border-[#5B6CF6]/40 dark:border-white/5 dark:hover:border-[#8B7CFF]/40'
        )}
      >
        <button
          type="button"
          onClick={() => onSelectMonth(monthAnchor)}
          className={cn(
            'mb-3 flex w-full items-center justify-between text-left'
          )}
        >
          <span className={cn('text-base font-bold tracking-tight transition-colors', monthSelected ? 'text-[#5B47E8] dark:text-[#8B7CFF]' : 'text-[#0f172b] group-hover:text-[#5B47E8] dark:text-white dark:group-hover:text-[#8B7CFF]')}>{monthTitle}</span>
          <span className="rounded-full bg-[#EEF2FF] px-2.5 py-0.5 text-[10px] font-semibold tracking-wide text-[#5B47E8] dark:bg-[#1C1F3F] dark:text-[#8B7CFF]">{year}</span>
        </button>

        <div>
          <div className="mb-1.5 grid grid-cols-7 gap-1 text-center text-[10px] font-semibold uppercase tracking-wider text-slate-400">
            {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d) => (
              <span key={d}>{d}</span>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-y-0.5">
            {miniMonth.map((cell) => {
              const key = format(cell.date, 'yyyy-MM-dd');
              const isSelected = selectedDate && cell.inCurrentMonth ? isSameDay(selectedDate, cell.date) : false;
              const dotKind = itemKindByDay.get(key) ?? null;
              const attendance =
                typeof attendanceByDay === 'object' && attendanceByDay ? attendanceByDay[key] : undefined;

              return (
                <button
                  key={cell.date.toISOString()}
                  type="button"
                  onClick={() => onSelectDate(cell.date)}
                  className={cn(
                    CALENDAR_YEAR_DATE_CELL_CLASS.base,
                    !cell.inCurrentMonth && CALENDAR_YEAR_DATE_CELL_CLASS.outsideMonth,
                    cell.inCurrentMonth && !isSelected && CALENDAR_YEAR_DATE_CELL_CLASS.currentMonthIdle,
                    cell.inCurrentMonth && !isSelected && CALENDAR_YEAR_DATE_CELL_CLASS.currentMonthHover,
                    cell.isToday && !isSelected && cell.inCurrentMonth && CALENDAR_YEAR_DATE_CELL_CLASS.today,
                    isSelected && CALENDAR_YEAR_DATE_CELL_CLASS.selected
                  )}
                >
                  {cell.date.getDate()}
                  {attendance === 'present' ? (
                    <span className={cn('absolute bottom-0.5 h-1 w-1 rounded-full', isSelected ? CALENDAR_YEAR_DATE_CELL_CLASS.selectedDot : 'bg-emerald-500')} title="Present" />
                  ) : attendance === 'absent' ? (
                    <span className={cn('absolute bottom-0.5 h-1 w-1 rounded-full', isSelected ? CALENDAR_YEAR_DATE_CELL_CLASS.selectedDot : 'bg-rose-500')} title="Absent" />
                  ) : attendance === 'weekoff' ? (
                    <span className={cn('absolute bottom-0.5 h-1 w-1 rounded-full', isSelected ? CALENDAR_YEAR_DATE_CELL_CLASS.selectedDot : 'bg-slate-400')} title="Weekoff" />
                  ) : attendance === 'holiday' ? (
                    <span className={cn('absolute bottom-0.5 h-1 w-1 rounded-full', isSelected ? CALENDAR_YEAR_DATE_CELL_CLASS.selectedDot : 'bg-indigo-500')} title="Holiday" />
                  ) : dotKind ? (
                    <span className={cn('absolute bottom-0.5 h-1 w-1 rounded-full', isSelected ? CALENDAR_YEAR_DATE_CELL_CLASS.selectedDot : KIND_DOT[dotKind])} />
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  });

  return (
    <section className="h-full flex flex-col">
      <div className="flex-1 overflow-y-auto custom-scrollbar">
        <div
          className={cn(
            'grid grid-cols-1 gap-5 pb-12 sm:grid-cols-2',
            compact ? 'lg:grid-cols-2 2xl:grid-cols-3' : 'lg:grid-cols-3 xl:grid-cols-4',
          )}
        >
          {monthCards}
        </div>
      </div>
    </section>
  );
}
